import { useMutation, useQuery, useQueryClient } from "react-query";
import { createPurchaseOrder } from "../mutations/purchase_orders.mutations";
import {
  fetchPurchaseOrder,
  fetchPurchaseOrderPaginated,
} from "../queries/purchase_order.queries";
import { PaginatedFilter } from "../types/axios.types";
import { useCenters } from "./centers.hooks";
import { useConnectedUser } from "./user.hooks";

export type usePrepPurchaseOrderProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data: any) => void;
  onError?: () => void;
};

export const usePrepPurchaseOrder = ({
  onSuccess,
  onError,
}: usePrepPurchaseOrderProps) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(createPurchaseOrder, {
    onSuccess: (data) => {
      queryClient.invalidateQueries("purchase-orders");
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
  return { mutate, isLoading };
};

export const usePurchaseOrderPaginated = (
  page?: number,
  size?: number,
  search?: string,
  filters?: PaginatedFilter,
  sortBy?: string[]
) => {
  const user = useConnectedUser();
  if (user == null) {
    throw new Error("User not connected");
  }
  const { center } = useCenters();
  const centerId = user.isSuperAdmin ? undefined : center?.id;

  const query = useQuery(
    ["purchase-orders", page, size, search, filters, sortBy, centerId],
    () => {
      try {
        return fetchPurchaseOrderPaginated(
          page,
          size,
          search,
          filters,
          sortBy,
          centerId
        );
      } catch (err) {
        return null;
      }
    },
    { keepPreviousData: true }
  );
  return query?.data;
};

export const usePurchaseOrder = ({
  id,
}: {
  id: string | number | undefined;
}) => {
  return useQuery(["purchase-orders", "detail", id], () => {
    try {
      return fetchPurchaseOrder({ id });
    } catch (err) {
      return null;
    }
  });
};
